import { combineEpics, ofType } from 'redux-observable';
import { mergeMap, debounceTime } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import _get from 'lodash/get';
import scribeSavedPoiDuck from 'ducks/scribe-saved-poi-duck';
import { userInfoSelector } from '../../selectors/app-selector';
import { currentEditingPOISelector } from '../../selectors/geo-selectors';
import {
  getCommonFacet,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_SAVED_POI_EVENTS,
  SCRIBE_STATE_NAMES,
  scribeTrackEvent,
} from '../../applications/scribe/instrumentations/track';
import { getPickUpAndDropOffPoiFromDelivery } from './scribe-epics';

const savePoiBookmarkEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.SAVED_POI_BOOKMARK),
    mergeMap(action => {
      const { state, isSavedPOI, delivery, isPickUp } = action;
      const userInfo = userInfoSelector(state$.value);
      const {
        pickUpLocationId,
        dropOffLocationIds,
      } = getPickUpAndDropOffPoiFromDelivery(_get(delivery, 'detail'));
      scribeTrackEvent({
        stateName: state,
        eventName: SCRIBE_SAVED_POI_EVENTS.SAVED_POI_BOOKMARK,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.POI_TYPE]: isPickUp ? 'PICKUP' : 'DROPOFF',
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: isSavedPOI,
          [SCRIBE_EVENT_PARAMS.PICKUP_POI_ID]: pickUpLocationId,
          [SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: dropOffLocationIds,
        },
      });
      return EMPTY;
    }),
  );

const extractPOIDetails = poi => [
  _get(poi, 'label', ''),
  _get(poi, 'id', ''),
  _get(poi, 'sp_ext_info.address_details', ''),
  _get(poi, 'sp_ext_info.note_to_driver', ''),
  _get(poi, 'sp_ext_info.contact_name', ''),
  _get(poi, 'sp_ext_info.contact_number', ''),
];

const startEditingSavedPOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.START_EDITING_SAVED_POI),
    mergeMap(() => {
      const userInfo = userInfoSelector(state$.value);
      const { originalPOI } = currentEditingPOISelector(state$.value);
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_SAVED_POI_DETAILS,
        eventName: SCRIBE_SAVED_POI_EVENTS.DEFAULT,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.EDIT_SAVED_POI_ORIGINAL]: extractPOIDetails(
            originalPOI,
          ),
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: !!_get(originalPOI, 'label'),
        },
      });
      return EMPTY;
    }),
  );

const cancelEditingSavedPOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.CANCEL_EDITING_SAVED_POI),
    mergeMap(() => {
      const userInfo = userInfoSelector(state$.value);
      const { originalPOI } = currentEditingPOISelector(state$.value);

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_SAVED_POI_DETAILS,
        eventName: SCRIBE_SAVED_POI_EVENTS.CANCEL,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: !!_get(originalPOI, 'label'),
        },
      });
      return EMPTY;
    }),
  );

const editSavedPOIFieldEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.EDIT_SAVED_POI_FIELD),
    debounceTime(300),
    mergeMap(action => {
      const { fieldName, fieldValue } = action;
      const userInfo = userInfoSelector(state$.value);
      const { originalPOI } = currentEditingPOISelector(state$.value);

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_SAVED_POI_DETAILS,
        eventName: `EDIT_SAVED_POI_${(fieldName || '').toUpperCase()}`,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.EDIT_SAVED_POI_VALUE]: fieldValue,
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: !!_get(originalPOI, 'label'),
        },
      });
      return EMPTY;
    }),
  );

const savePOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.SAVE_POI),
    mergeMap(() => {
      const userInfo = userInfoSelector(state$.value);
      const { originalPOI, currentPOI } = currentEditingPOISelector(
        state$.value,
      );

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_SAVED_POI_DETAILS,
        eventName: SCRIBE_SAVED_POI_EVENTS.SAVE,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: !!_get(originalPOI, 'label'),
          [SCRIBE_EVENT_PARAMS.EDIT_SAVED_POI_SAVE]: extractPOIDetails(
            currentPOI,
          ),
        },
      });
      return EMPTY;
    }),
  );

const deleteSavedPOIEpic = (action$, state$) =>
  action$.pipe(
    ofType(scribeSavedPoiDuck.types.DELETE_SAVED_POI),
    mergeMap(() => {
      const userInfo = userInfoSelector(state$.value);

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_SAVED_POI_DETAILS,
        eventName: SCRIBE_SAVED_POI_EVENTS.REMOVE,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.IS_SAVED_POI]: true,
        },
      });
      return EMPTY;
    }),
  );

export default combineEpics(
  savePoiBookmarkEpic,
  startEditingSavedPOIEpic,
  cancelEditingSavedPOIEpic,
  editSavedPOIFieldEpic,
  savePOIEpic,
  deleteSavedPOIEpic,
);
